import React from "react";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

interface ChatHistorySearchFilterProps {
  searchText: string;
  userFilter: string;
  startDate: string;
  endDate: string;
  totalCount: number;
  selectedDatePreset: string;
  onSearchTextChange: (value: string) => void;
  onUserFilterChange: (value: string) => void;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
  onDatePresetSelect: (preset: string) => void;
  onSearch: () => void;
  onReset: () => void;
}

const DATE_PRESETS = [
  { label: "전체", value: "ALL" },
  { label: "오늘", value: "TODAY" },
  { label: "3일", value: "3DAYS" },
  { label: "7일", value: "7DAYS" },
  { label: "1개월", value: "1MONTH" },
  { label: "6개월", value: "6MONTHS" },
  { label: "1년", value: "1YEAR" },
  { label: "직접선택", value: "CUSTOM" },
];

function ChatHistorySearchFilter({
  searchText,
  userFilter,
  startDate,
  endDate,
  totalCount,
  selectedDatePreset,
  onSearchTextChange,
  onUserFilterChange,
  onStartDateChange,
  onEndDateChange,
  onDatePresetSelect,
  onSearch,
  onReset,
}: ChatHistorySearchFilterProps) {
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^\d]/g, "");

    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    } else if (numbers.length <= 11) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
    }
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneNumberChange = (value: string) => {
    const formatted = formatPhoneNumber(value);
    onUserFilterChange(formatted);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* 검색바와 필터 설정 버튼 */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="채팅 내용과 제목을 검색하세요..."
            value={searchText}
            onChange={(e) => onSearchTextChange(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                onSearch();
              }
            }}
            style={{ border: '1px solid #d4d4d8' }}
            className="h-[40px] w-[400px] rounded-md bg-white px-3 text-sm outline-none focus:border-black"
          />
          <span className="text-sm font-semibold text-blue-600 whitespace-nowrap">
            총 {totalCount}개
          </span>
        </div>
        <button
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          className="flex items-center gap-1 whitespace-nowrap rounded bg-black px-4 h-[40px] text-sm font-medium text-white transition-colors hover:bg-gray-800"
        >
          필터설정
          {isFilterOpen ? (
            <ChevronUpIcon className="h-4 w-4" />
          ) : (
            <ChevronDownIcon className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* 필터 영역 */}
      {isFilterOpen && (
        <div className="flex flex-col gap-4 bg-gray-50 p-4 rounded-md">
          {/* 전화번호 필터 */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">사용자</span>
              <input
                type="text"
                placeholder="전화번호 입력"
                value={userFilter}
                onChange={(e) => handlePhoneNumberChange(e.target.value)}
                style={{ border: '1px solid #d4d4d8' }}
                className="h-[40px] w-[200px] rounded-md bg-white px-3 text-sm outline-none focus:border-black"
              />
            </div>
          </div>

          {/* 기간 선택 */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">기간</span>
              <div className="flex gap-2">
                {DATE_PRESETS.map((preset) => {
                  const isSelected = selectedDatePreset === preset.value;
                  return (
                    <button
                      key={preset.value}
                      onClick={() => onDatePresetSelect(preset.value)}
                      className={`rounded px-3 h-[40px] text-sm font-medium transition-colors ${
                        isSelected
                          ? "bg-blue-500 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {preset.label}
                    </button>
                  );
                })}
              </div>

              {selectedDatePreset === "CUSTOM" && (
                <div className="flex items-center gap-2 rounded border border-gray-300 px-3 h-[40px] bg-white">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => onStartDateChange(e.target.value)}
                    className="border-none outline-none text-sm bg-transparent"
                  />
                  <span className="text-gray-500">~</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => onEndDateChange(e.target.value)}
                    className="border-none outline-none text-sm bg-transparent"
                  />
                </div>
              )}
            </div>

            <button
              onClick={onReset}
              className="whitespace-nowrap rounded bg-black px-4 h-[40px] text-sm font-medium text-white transition-colors hover:bg-gray-800"
            >
              초기화
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatHistorySearchFilter;
